// 試穿計數 — the 試衣 tab's main screen. Staff scan each hang tag as the piece
// goes into the fitting room; one scan = one 試穿. Nothing else is asked.
//
// WHY SO LITTLE. The previous 試衣 flow wanted a 顧客卡 (身高/體重/體型) and then
// an outcome per garment (買了 / 沒買 + reason). Its first month at the counter
// produced ZERO 試穿 rows. The signal worth having is which products customers
// pick up, and whether those turn into sales — and sales are already recorded,
// so 試穿 vs 售出 per SKU is computed server-side (GET /tryons/summary) rather
// than asked of a part-timer with a customer waiting.
//
// The scanner itself lives on NokePosPage (it must never unmount); the page
// routes a scan here through the imperative `record(code)` handle while the
// 試衣 tab is active.
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

/** The garment a scan resolved to — enough to open the 商品答案卡. */
export interface TryonItem {
  variant_id: number;
  sku: string;
  product_title: string;
  vendor: string;
  color: string;
  size: string;
  price: number;
  qty_on_hand: number;
}

interface TryonRow {
  id: number;
  variant_id: number;
  sku: string;
  product_title: string;
  color: string;
  size: string;
  staff: string | null;
  created_at: string;
}

interface LogEntry {
  key: string;
  status: 'saving' | 'ok' | 'failed';
  code: string;
  clientRef: string;
  row?: TryonRow;
  item?: TryonItem;
  error?: string;
}

export interface SummaryItem {
  sku: string;
  product_title: string;
  variant_id: number;
  tryons: number;
  sold: number;
  by_variant: { color: string; size: string; n: number }[];
  image_url: string | null;
}

interface Summary {
  days: number;
  total_tryons: number;
  total_sold: number;
  items: SummaryItem[];
}

export interface TryonPanelHandle {
  record: (code: string) => void;
}

interface Props {
  /** True while the 試衣 tab is on screen — refreshes lists another device may have changed. */
  active: boolean;
  /** Success beep/vibrate, shared with the rest of the page. */
  onScanned?: () => void;
  /** Open the 商品答案卡 (材質、尺寸表、加入結帳) for a scanned piece. */
  onOpenCard?: (item: TryonItem) => void;
}

// A 試穿 that many customers pick up and nobody buys is the most useful line on
// this screen: the product pulls people in and then something (fit, price,
// fabric) loses them. Flag it once the count stops being noise.
export const HIGH_INTEREST_NO_SALE_MIN = 3;

const DAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '今天' },
  { value: 7, label: '近 7 天' },
  { value: 30, label: '近 30 天' },
];

const apiMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error?.message || err?.response?.data?.message || fallback;

const newClientRef = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tryon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const timeOf = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const variantText = (color?: string | null, size?: string | null): string =>
  [color, size].filter((s) => s && String(s).trim()).join(' / ') || '—';

/** 「最多試：黑 / L ×3」 — only when one variant clearly leads. */
export const topVariantText = (item: SummaryItem): string | null => {
  const top = item.by_variant[0];
  if (!top || item.by_variant.length < 2 || top.n < 2) return null;
  return `最多試：${variantText(top.color, top.size)} ×${top.n}`;
};

const TryonPanel = forwardRef<TryonPanelHandle, Props>(({ active, onScanned, onOpenCard }, ref) => {
  const { enqueueSnackbar } = useSnackbar();
  const [log, setLog] = useState<LogEntry[]>([]);
  const [totalToday, setTotalToday] = useState<number | null>(null);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [lastSkuToday, setLastSkuToday] = useState<number | null>(null);
  const [undoing, setUndoing] = useState<number | null>(null);

  const [days, setDays] = useState(1);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const summarySeq = useRef(0);

  const loadToday = useCallback(async () => {
    try {
      const res = await api.get('/api/noke/tryons/today');
      const rows: TryonRow[] = res.data.items || [];
      setTotalToday(res.data.total_today ?? rows.length);
      // Keep lines that have not reached the server yet; replace the rest, but
      // reuse a line's existing entry so its key (and the 已記錄 card, and the
      // 商品資訊 item) survive the refresh.
      setLog((prev) => {
        const known = new Map(prev.filter((e) => e.row).map((e) => [e.row!.id, e]));
        return [
          ...prev.filter((e) => e.status !== 'ok'),
          ...rows.map((row) => {
            const existing = known.get(row.id);
            return existing
              ? { ...existing, row }
              : { key: `row-${row.id}`, status: 'ok' as const, code: '', clientRef: '', row };
          }),
        ];
      });
    } catch {
      /* the scan path still works; the list just stays as it was */
    }
  }, []);

  const loadSummary = useCallback(async (d: number) => {
    const seq = ++summarySeq.current;
    setSummaryLoading(true);
    try {
      const res = await api.get(`/api/noke/tryons/summary?days=${d}`);
      if (seq !== summarySeq.current) return;
      setSummary(res.data);
      setSummaryError(null);
    } catch (err) {
      if (seq !== summarySeq.current) return;
      setSummaryError(apiMessage(err, '讀不到試穿排行，請確認網路'));
    } finally {
      if (seq === summarySeq.current) setSummaryLoading(false);
    }
  }, []);

  // Another phone at the counter may have scanned meanwhile.
  useEffect(() => {
    if (active) loadToday();
  }, [active, loadToday]);

  useEffect(() => {
    if (active) loadSummary(days);
  }, [active, days, loadSummary]);

  const send = useCallback(
    async (entry: LogEntry) => {
      try {
        const res = await api.post('/api/noke/tryons', { code: entry.code, client_ref: entry.clientRef });
        const row: TryonRow = res.data.tryon;
        const item: TryonItem = res.data.item;
        setLog((prev) => {
          // A replay can return a row that is already listed — never show it twice.
          const rest = prev.filter((e) => e.key !== entry.key && e.row?.id !== row.id);
          return [{ ...entry, status: 'ok', row, item, error: undefined }, ...rest];
        });
        // A replay after a lost response (entry was 'failed') was never counted
        // here, so it still adds one; a replay of a counted row does not.
        if (!res.data.replayed || entry.status === 'failed') setTotalToday((n) => (n ?? 0) + 1);
        setLastKey(entry.key);
        setLastSkuToday(res.data.sku_today ?? null);
        onScanned?.();
        loadSummary(days);
      } catch (err: any) {
        if (err?.response?.status === 404 || err?.response?.status === 400) {
          // Not a garment we know — nothing to retry, so don't leave a dead line.
          setLog((prev) => prev.filter((e) => e.key !== entry.key));
          enqueueSnackbar(apiMessage(err, `找不到條碼 ${entry.code}`), { variant: 'error' });
          return;
        }
        setLog((prev) =>
          prev.map((e) =>
            e.key === entry.key ? { ...e, status: 'failed', error: apiMessage(err, '沒有記錄成功，請重試') } : e
          )
        );
      }
    },
    [days, enqueueSnackbar, loadSummary, onScanned]
  );

  const record = useCallback(
    (raw: string) => {
      const code = raw.trim();
      if (!code) return;
      const clientRef = newClientRef();
      const entry: LogEntry = { key: clientRef, status: 'saving', code, clientRef };
      setLog((prev) => [entry, ...prev]);
      send(entry);
    },
    [send]
  );

  useImperativeHandle(ref, () => ({ record }), [record]);

  const retry = (entry: LogEntry) => {
    // Same client_ref: if the first attempt did land, the server replays it.
    setLog((prev) => prev.map((e) => (e.key === entry.key ? { ...e, status: 'saving', error: undefined } : e)));
    send({ ...entry, status: 'saving' });
  };

  const undo = async (entry: LogEntry) => {
    if (!entry.row) return;
    const id = entry.row.id;
    setUndoing(id);
    try {
      await api.delete(`/api/noke/tryons/${id}`);
      setLog((prev) => prev.filter((e) => e.row?.id !== id));
      setTotalToday((n) => Math.max(0, (n ?? 1) - 1));
      if (lastKey === entry.key) {
        setLastKey(null);
        setLastSkuToday(null);
      }
      enqueueSnackbar('已復原這筆試穿', { variant: 'info' });
      loadSummary(days);
    } catch (err) {
      enqueueSnackbar(apiMessage(err, '復原失敗，請再試一次'), { variant: 'error' });
    } finally {
      setUndoing(null);
    }
  };

  const last = lastKey ? log.find((e) => e.key === lastKey && e.status === 'ok') : undefined;

  return (
    <Box>
      {/* 今天試穿 — the number staff see grow is the reason to keep scanning. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            今天試穿
          </Typography>
          <Typography variant="h4" component="span" sx={{ fontWeight: 700 }}>
            {totalToday ?? '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            件
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          顧客拿衣服進試衣間時，每一件掃一次吊牌就好。掃錯了按「復原」。
        </Typography>

        {last?.row && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mt: 1.5, alignItems: 'center' }}
            action={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={undoing === last.row.id ? <CircularProgress size={14} /> : <UndoIcon />}
                  disabled={undoing === last.row.id}
                  onClick={() => undo(last)}
                >
                  復原
                </Button>
                {onOpenCard && last.item && (
                  <Button size="small" color="inherit" startIcon={<InfoOutlinedIcon />} onClick={() => onOpenCard(last.item!)}>
                    商品資訊
                  </Button>
                )}
              </Box>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              已記錄試穿
            </Typography>
            <Typography variant="body2">{last.row.product_title}</Typography>
            <Typography variant="caption" display="block">
              SKU {last.row.sku} · {variantText(last.row.color, last.row.size)}
              {lastSkuToday != null && ` · 這款今天第 ${lastSkuToday} 次試穿`}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* 今日紀錄 — where a misscan gets undone. */}
      {log.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            今日紀錄
          </Typography>
          {log.slice(0, 30).map((e) => (
            <Box
              key={e.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: 1, borderColor: 'divider' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ width: 40, flexShrink: 0 }}>
                {e.row ? timeOf(e.row.created_at) : ''}
              </Typography>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {e.row?.product_title || e.code}
                </Typography>
                <Typography variant="caption" color={e.status === 'failed' ? 'error' : 'text.secondary'} noWrap display="block">
                  {e.status === 'saving' && '記錄中…'}
                  {e.status === 'failed' && (e.error || '沒有記錄成功')}
                  {e.status === 'ok' && e.row && `SKU ${e.row.sku} · ${variantText(e.row.color, e.row.size)}`}
                </Typography>
              </Box>
              {e.status === 'saving' && <CircularProgress size={18} />}
              {e.status === 'failed' && (
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => retry(e)}>
                  重試
                </Button>
              )}
              {e.status === 'ok' && e.row && (
                <Tooltip title="掃錯了，刪掉這筆">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="復原"
                      disabled={undoing === e.row.id}
                      onClick={() => undo(e)}
                    >
                      {undoing === e.row.id ? <CircularProgress size={16} /> : <UndoIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          ))}
          {log.length > 30 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              只顯示最近 30 筆
            </Typography>
          )}
        </Paper>
      )}

      {/* 試穿排行 — 試穿 next to 售出 for the same window is the whole signal. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            試穿排行
          </Typography>
          {summaryLoading && <CircularProgress size={16} />}
        </Box>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={days}
          onChange={(_e, v) => v && setDays(v)}
          sx={{ mb: 1.5 }}
        >
          {DAY_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {summaryError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {summaryError}
          </Alert>
        )}

        {summary && summary.items.length === 0 && !summaryError && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            這段期間還沒有試穿紀錄。
          </Typography>
        )}

        {summary && summary.items.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            共 {summary.items.length} 款、{summary.total_tryons} 次試穿；這些商品同期間在櫃上售出 {summary.total_sold} 件
          </Typography>
        )}

        {summary?.items.map((item) => {
          const flag = item.tryons >= HIGH_INTEREST_NO_SALE_MIN && item.sold === 0;
          const top = topVariantText(item);
          return (
            <Box
              key={item.sku}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1, borderTop: 1, borderColor: 'divider' }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 64,
                  flexShrink: 0,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                }}
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {item.product_title}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  SKU {item.sku}
                  {top && ` · ${top}`}
                </Typography>
                {flag && (
                  <Chip size="small" color="warning" variant="outlined" label="試穿多、還沒賣出" sx={{ mt: 0.5, height: 20 }} />
                )}
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="h6" component="div" sx={{ lineHeight: 1.1 }}>
                  {item.tryons}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  試穿
                </Typography>
                <Typography variant="caption" color={item.sold > 0 ? 'success.main' : 'text.secondary'} display="block">
                  售出 {item.sold}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          售出＝同期間在櫃上結帳的件數（不含贈品）。
        </Typography>
      </Paper>
    </Box>
  );
});

TryonPanel.displayName = 'TryonPanel';

export default TryonPanel;
